import { Button, Tag } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { withinSection } from '../../../shared/components/section-trail';
import { JudgementHistory, ResolutionHistory } from '../components/DistanceHistory';
import { DistanceSources } from '../components/DistanceSources';
import { ResolveGapDrawer } from '../components/ResolveGapDrawer';
import type { DistanceTripDetail } from '../distance-agreement.api';
import { CLOSE_OUT_TAB_LABELS, closeOutTabPath } from '../distance-agreement.paths';
import { useTripDistance } from '../hooks/use-trip-distance';
import { stateExplanation, stateTag } from '../policies/distance-labels.policy';

const BREADCRUMBS = withinSection('close-out', { label: CLOSE_OUT_TAB_LABELS.gaps, to: closeOutTabPath('gaps') });

interface TripDistanceViewProps {
  trip: DistanceTripDetail;
  detail: Pick<ReturnType<typeof useTripDistance>, 'canResolve' | 'isResolving' | 'refusal' | 'clearRefusal' | 'resolve'>;
}

function TripDistanceView({ trip, detail }: TripDistanceViewProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const tag = stateTag(trip.state);
  const isAnswerable = trip.state === 'gap' || trip.state === 'resolved';

  function openDrawer() {
    detail.clearRefusal();
    setIsDrawerOpen(true);
  }

  return (
    <>
      <PageHeader
        breadcrumbs={BREADCRUMBS}
        title={`${trip.number} · ${trip.truckRegistration ?? 'No truck'}`}
        description={stateExplanation(trip) ?? 'The odometer and GPS distances agree.'}
        actions={
          <div className="flex items-center gap-3">
            <Tag color={tag.color}>{tag.label}</Tag>
            {detail.canResolve && isAnswerable && (
              <Button type="primary" onClick={openDrawer}>
                {trip.state === 'resolved' ? 'Change answer' : 'Resolve gap'}
              </Button>
            )}
          </div>
        }
      />
      <div className="flex flex-col gap-8">
        <DistanceSources trip={trip} />
        <ResolutionHistory resolutions={trip.resolutions} />
        <JudgementHistory history={trip.history} />
      </div>
      <ResolveGapDrawer
        open={isDrawerOpen}
        reason={trip.reason}
        error={detail.refusal}
        submitting={detail.isResolving}
        onResolve={detail.resolve}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
}

/** One closed-out trip's comparison: every figure with where it came from, how it was answered, and the action to answer it. */
export function TripDistancePage() {
  const { tripId = '' } = useParams();
  const detail = useTripDistance(tripId);
  const { state } = detail;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading distance comparison" breadcrumbs={BREADCRUMBS} />;
  if (state.status === 'forbidden') {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="You can't see close-out"
        description="Comparing a trip's distances needs the permission to view them. Ask an administrator."
      />
    );
  }
  if (state.status === 'not_found') {
    return (
      <EmptyState icon={FileX} title="No comparison for this trip" description="The trip has not been received at the yard yet, or the link is out of date." />
    );
  }
  if (state.status === 'error') throw state.error;
  return <TripDistanceView trip={state.trip} detail={detail} />;
}
